import { useCallback, useEffect, useMemo, useRef } from "react";
import { useSelector } from "react-redux";

/**
 * Base listing controller:
 * - calls onInit/onReset once
 * - loads first page (0, defaultPageSize) on mount
 * - provides pagination handlers
 *
 * No cookies, no scroll restore/persist.
 */
export function useListingTableControllerBase({
  sliceSelector = (state) => state.userSelections,
  paginationReduxKey,
  loadPage, // (pageNumber, pageSize) => void|Promise
  onInit,
  onReset,
  defaultPageSize = 10,
  scrollContainerRef, // modal body/table wrapper ref
  scrollOnPageChange = true,
}) {
  const slice = useSelector(sliceSelector);
  const pagination = slice?.[paginationReduxKey];

  const loadPageRef = useRef(loadPage);
  useEffect(() => {
    loadPageRef.current = loadPage;
  }, [loadPage]);

  const paginationRef = useRef(pagination);
  useEffect(() => {
    paginationRef.current = pagination;
  }, [pagination]);

  const scrollToTop = useCallback(() => {
    if (!scrollOnPageChange) return;

    const el = scrollContainerRef?.current;
    if (el && typeof el.scrollTo === "function") {
      el.scrollTo({ top: 0 });
    } else if (el) {
      el.scrollTop = 0;
    }
  }, [scrollContainerRef, scrollOnPageChange]);

  const refresh = useCallback(() => {
    const p = paginationRef.current;
    const pageNumber = p?.pageNumber ?? 0;
    const pageSize = p?.pageSize ?? defaultPageSize;
    loadPageRef.current(pageNumber, pageSize);
  }, [defaultPageSize]);

  const onPageChange = useCallback((_e, pageNumOneBased) => {
    const pageSize = paginationRef.current?.pageSize ?? defaultPageSize;
    loadPageRef.current(pageNumOneBased - 1, pageSize);
    scrollToTop();
  }, [defaultPageSize, scrollToTop]);

  const onPageSizeChange = useCallback((pageSizeVal) => {
    loadPageRef.current(0, pageSizeVal);
    scrollToTop();
  }, [scrollToTop]);

  useEffect(() => {
    onReset?.();
    onInit?.();
    loadPageRef.current(0, defaultPageSize);
  }, []);

  return useMemo(
    () => ({ pagination, onPageChange, onPageSizeChange, refresh }),
    [pagination, onPageChange, onPageSizeChange, refresh]
  );
}
